/**
 * Why Choose Note carousel - Custom element
 * Handles the reasons carousel with navigation
 */

class WhyChooseNoteCarousel extends HTMLElement {
  constructor() {
    super();
    
    this.carousel = this.querySelector('[data-carousel]');
    this.imageWrappers = Array.from(this.querySelectorAll('.why-choose-note__card-image-wrapper'));
    this.contentBlocks = Array.from(this.querySelectorAll('.why-choose-note__card-content-block'));
    this.dots = Array.from(this.querySelectorAll('.why-choose-note__dot'));
    this.prevButton = this.querySelector('.why-choose-note__nav--prev');
    this.nextButton = this.querySelector('.why-choose-note__nav--next');
    
    this.currentIndex = 0;
    this.isTransitioning = false;
    
    this.init();
  }

  init() {
    if (!this.carousel || this.imageWrappers.length === 0) return;

    // Set initial active states
    this.updateActiveStates();
    
    // Add event listeners for navigation buttons
    this.prevButton?.addEventListener('click', () => this.navigate('prev'));
    this.nextButton?.addEventListener('click', () => this.navigate('next'));
    
    // Add event listeners for pagination dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (index !== this.currentIndex && !this.isTransitioning) {
          this.currentIndex = index;
          this.updateActiveStates();
          this.updateCarousel();
        }
      });
    });
    
    // Initial carousel position
    this.updateCarousel();
    
    // Handle keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigate('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigate('next');
      }
    });
    
    // Update on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateCarousel();
      }, 250);
    });
  }

  navigate(direction) {
    if (this.isTransitioning) return;
    
    const previousIndex = this.currentIndex;
    
    if (direction === 'prev') {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
    } else {
      this.currentIndex = Math.min(this.imageWrappers.length - 1, this.currentIndex + 1);
    }
    
    // Only update if index changed
    if (previousIndex !== this.currentIndex) {
      this.updateActiveStates();
      this.updateCarousel();
    }
  }

  updateActiveStates() {
    const isLastCard = this.currentIndex === this.imageWrappers.length - 1;
    
    // Update content blocks (fade effect)
    this.contentBlocks.forEach((block, index) => {
      if (index === this.currentIndex) {
        block.classList.add('is-active');
      } else {
        block.classList.remove('is-active');
      }
    });
    
    // Update image wrappers (slide effect with visibility)
    this.imageWrappers.forEach((wrapper, index) => {
      wrapper.classList.remove('is-active', 'show-before', 'show-after');
      wrapper.setAttribute('aria-current', 'false');
      
      // Determine which images should be visible
      if (index === this.currentIndex) {
        // Current active image
        wrapper.classList.add('is-active');
        wrapper.setAttribute('aria-current', 'true');
      } else if (isLastCard && index === this.currentIndex - 1) {
        // Show previous image only when last image is active
        wrapper.classList.add('show-before');
      } else if (!isLastCard && index === this.currentIndex + 1) {
        // Show next image when not on last image
        wrapper.classList.add('show-after');
      }
    });

    // Update pagination dots
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.setAttribute('aria-current', 'false');
      }
    });
    
    // Update navigation buttons
    this.updateNavigationButtons();
  }

  updateCarousel() {
    if (!this.carousel || this.imageWrappers.length === 0) return;

    this.isTransitioning = true;

    const activeWrapper = this.imageWrappers[this.currentIndex];
    
    if (!activeWrapper) return;

    const isFirstCard = this.currentIndex === 0;
    const isLastCard = this.currentIndex === this.imageWrappers.length - 1;
    const wrapperOffsetLeft = activeWrapper.offsetLeft;
    const containerWidth = this.carousel.parentElement.offsetWidth;
    const wrapperWidth = activeWrapper.offsetWidth;
    
    let translateX;
    
    if (isFirstCard) {
      // First image: no translation (position 0)
      translateX = 0;
    } else if (isLastCard) {
      // Last image: align to the right
      const rightEdge = wrapperOffsetLeft + wrapperWidth;
      translateX = containerWidth - rightEdge;
    } else {
      // Middle images: align to the start (same as first image position)
      translateX = -wrapperOffsetLeft;
    }
    
    // Apply transform to carousel
    this.carousel.style.transform = `translateX(${translateX}px)`;
    
    // Reset transitioning flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }

  updateNavigationButtons() {
    // Disable prev button if at start
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    
    // Disable next button if at end
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.imageWrappers.length - 1;
    }
  }
}

// Register the custom element
customElements.define('why-choose-note-carousel', WhyChooseNoteCarousel);
